// src/services/ceraFundicaoService.js
import { pb } from "./pocketbase";
import { produtoDadosService } from "./produtoDadosService";
import { cleanSku, getBaseSku } from "../utils/skuUtils";

/**
 * Gera um número de pedido eterno, ordenável e sem colisões para reposição via estoque de cera.
 * Padrão: EST-ddmmaaaahhmmss (ex: EST-24092026174512)
 */
export function generateEstoqueOrderNumber(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  const d = pad(date.getDate());
  const m = pad(date.getMonth() + 1);
  const y = date.getFullYear();
  const h = pad(date.getHours());
  const min = pad(date.getMinutes());
  const s = pad(date.getSeconds());
  return `EST-${d}${m}${y}${h}${min}${s}`;
}

export const ceraFundicaoService = {
  /**
   * Envia peças em cera diretamente para a etapa de Fundição (Status: INJETADO).
   * 
   * - Gera o identificador EST-ddmmaaaahhmmss
   * - Cria os registros correspondentes em production_orders com status "INJETADO"
   * - Debita a quantidade exata de cera bruta e/ou pré-cravada do subestoque
   * - Registra no log de auditoria da gaveta
   * 
   * @param {Object} params
   * @param {string} params.sku - SKU da peça (pode ser SKU pai ou variação com aro)
   * @param {string} params.productName - Nome da joia
   * @param {number} params.qtdBruta - Quantidade de ceras brutas a fundir
   * @param {number} params.qtdPrecravada - Quantidade de ceras pré-cravadas a fundir
   * @param {string} [params.size] - Tamanho/Aro específico (para anéis)
   * @param {string} [params.gaveta] - Gaveta do molde
   * @param {string} [params.userName] - Nome do operador realizando o envio
   * @returns {Promise<{ success: boolean, orderNumber: string, createdOrders: Array, updatedSubestoque: Object }>}
   */
  async enviarParaFundicao({
    sku,
    productName = "",
    qtdBruta = 0,
    qtdPrecravada = 0,
    size = null,
    gaveta = "",
    userName = "Operador"
  }) {
    const numBruta = Math.max(0, parseInt(qtdBruta, 10) || 0);
    const numPrecravada = Math.max(0, parseInt(qtdPrecravada, 10) || 0);
    const totalQtd = numBruta + numPrecravada;

    if (totalQtd <= 0) {
      throw new Error("A quantidade de ceras para fundição deve ser maior que zero.");
    }

    if (!sku) {
      throw new Error("SKU inválido para envio de ceras.");
    }

    const clean = cleanSku(sku);
    const skuPai = getBaseSku(clean) || clean;
    const orderNumber = generateEstoqueOrderNumber();
    const createdOrders = [];

    // 1. Cria cada ordem individual em production_orders com status "INJETADO"
    // Ceras Brutas
    for (let i = 0; i < numBruta; i++) {
      const payload = {
        status: "INJETADO",
        production_step: "INJETADO",
        sku: clean,
        customer_name: "Solicitacao-Estoque",
        order_number: orderNumber,
        logistics_status: "SEM ETIQUETA",
        archived: false,
        certificatePrinted: false,
        isPE: true,
        isStockRequest: true,
        specs: {
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "bruta",
          size: size || null,
          gaveta: gaveta || "",
          exception: ""
        },
        full_data: {
          order: {
            number: orderNumber,
            customer: {
              name: "Solicitacao-Estoque"
            }
          },
          sku: clean,
          status: "INJETADO",
          production_step: "INJETADO",
          isPE: true,
          isStockRequest: true,
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "bruta",
          size: size || null,
          productName: productName || "",
          gaveta: gaveta || "",
          createdAt: new Date().toISOString()
        }
      };

      const record = await pb.collection("production_orders").create(payload);
      createdOrders.push(record);
    }

    // Ceras Pré-Cravadas
    for (let i = 0; i < numPrecravada; i++) {
      const payload = {
        status: "INJETADO",
        production_step: "INJETADO",
        sku: clean,
        customer_name: "Solicitacao-Estoque",
        order_number: orderNumber,
        logistics_status: "SEM ETIQUETA",
        archived: false,
        certificatePrinted: false,
        isPE: true,
        isStockRequest: true,
        specs: {
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "precravada",
          size: size || null,
          gaveta: gaveta || "",
          exception: ""
        },
        full_data: {
          order: {
            number: orderNumber,
            customer: {
              name: "Solicitacao-Estoque"
            }
          },
          sku: clean,
          status: "INJETADO",
          production_step: "INJETADO",
          isPE: true,
          isStockRequest: true,
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "precravada",
          size: size || null,
          productName: productName || "",
          gaveta: gaveta || "",
          createdAt: new Date().toISOString()
        }
      };

      const record = await pb.collection("production_orders").create(payload);
      createdOrders.push(record);
    }

    // 2. Debita o subestoque_cera de forma atômica e auditada (no aro específico se informado)
    const cleanSize = size ? String(size).trim() : null;
    const debitParams = cleanSize ? {
      deltaAro: {
        aro: cleanSize,
        deltaBruta: -numBruta || 0,
        deltaPrecravada: -numPrecravada || 0
      }
    } : {
      deltaBruta: -numBruta || 0,
      deltaPrecravada: -numPrecravada || 0
    };

    const debitRes = await produtoDadosService.updateSubestoqueCera(skuPai, {
      ...debitParams,
      tipoOperacao: "ENVIO_FUNDICAO_ESTOQUE",
      motivo: `Ordem #${orderNumber} (${totalQtd} un p/ Fundição - ${numBruta} brutas, ${numPrecravada} pré-cravadas${cleanSize ? ` no Aro ${cleanSize}` : ""})`,
      usuario: userName
    });

    return {
      success: true,
      orderNumber,
      totalQtd,
      createdOrders,
      updatedSubestoque: debitRes?.subestoque || null
    };
  },

  /**
   * Solicita injeção e produção de novas peças a partir do molde de borracha
   * (Quando NÃO há cera na gaveta ou para quantidades superiores ao saldo existente).
   * 
   * - Gera o identificador EST-ddmmaaaahhmmss
   * - Cria registros em production_orders com status "IMPRIMIR" (fila de injeção)
   * - NÃO debita o estoque físico de ceras da gaveta
   * - Registra no histórico do molde
   */
  async solicitarInjecaoEProducao({
    sku,
    productName = "",
    qtd = 1,
    tipoCera = "bruta", // "bruta" ou "precravada"
    size = null,
    gaveta = "",
    userName = "Operador"
  }) {
    const numQtd = Math.max(1, parseInt(qtd, 10) || 1);
    if (!sku) {
      throw new Error("SKU inválido para solicitação de injeção.");
    }

    const clean = cleanSku(sku);
    const skuPai = getBaseSku(clean) || clean;
    const orderNumber = generateEstoqueOrderNumber();
    const createdOrders = [];

    for (let i = 0; i < numQtd; i++) {
      const payload = {
        status: "IMPRIMIR",
        production_step: "IMPRIMIR",
        sku: clean,
        customer_name: "Solicitacao-Estoque",
        order_number: orderNumber,
        logistics_status: "SEM ETIQUETA",
        archived: false,
        certificatePrinted: false,
        isPE: true,
        isStockRequest: true,
        specs: {
          processo_fabricacao: "borracha",
          origem: "solicitacao_estoque",
          tipo_cera: tipoCera,
          size: size || null,
          gaveta: gaveta || "",
          exception: ""
        },
        full_data: {
          order: {
            number: orderNumber,
            customer: {
              name: "Solicitacao-Estoque"
            }
          },
          sku: clean,
          status: "IMPRIMIR",
          production_step: "IMPRIMIR",
          isPE: true,
          isStockRequest: true,
          processo_fabricacao: "borracha",
          origem: "solicitacao_estoque",
          tipo_cera: tipoCera,
          size: size || null,
          productName: productName || "",
          gaveta: gaveta || "",
          createdAt: new Date().toISOString()
        }
      };

      const record = await pb.collection("production_orders").create(payload);
      createdOrders.push(record);
    }

    // Registra no histórico do molde (sem alterar saldo físico)
    const cleanSize = size ? String(size).trim() : null;
    await produtoDadosService.updateSubestoqueCera(skuPai, {
      tipoOperacao: "SOLICITACAO_INJECAO_ESTOQUE",
      motivo: `Ordem #${orderNumber} (${numQtd} un p/ Injetar e Produzir - Cera ${tipoCera === "precravada" ? "Pré-Cravada" : "Bruta"}${cleanSize ? ` no Aro ${cleanSize}` : ""})`,
      usuario: userName
    });

    return {
      success: true,
      orderNumber,
      totalQtd: numQtd,
      createdOrders
    };
  },

  /**
   * Processamento unificado: permite mandar fundir ceras da gaveta (status INJETADO)
   * e/ou solicitar injeção de novas peças adicionais (status IMPRIMIR) sob o mesmo pedido EST-...
   */
  async processarProducaoEstoque({
    sku,
    productName = "",
    fundirBruta = 0,
    fundirPrecravada = 0,
    injetarQtd = 0,
    injetarTipoCera = "bruta",
    size = null,
    gaveta = "",
    userName = "Operador"
  }) {
    const numFundirBruta = Math.max(0, parseInt(fundirBruta, 10) || 0);
    const numFundirPrecravada = Math.max(0, parseInt(fundirPrecravada, 10) || 0);
    const numFundirTotal = numFundirBruta + numFundirPrecravada;
    const numInjetar = Math.max(0, parseInt(injetarQtd, 10) || 0);
    const totalGeral = numFundirTotal + numInjetar;

    if (totalGeral <= 0) {
      throw new Error("A quantidade total para produção deve ser maior que zero.");
    }
    if (!sku) {
      throw new Error("SKU inválido para produção de estoque.");
    }

    const clean = cleanSku(sku);
    const skuPai = getBaseSku(clean) || clean;
    const orderNumber = generateEstoqueOrderNumber();
    const createdOrders = [];

    // 1. Cria itens para Mandar Fundir (status: INJETADO)
    for (let i = 0; i < numFundirBruta; i++) {
      const payload = {
        status: "INJETADO",
        production_step: "INJETADO",
        sku: clean,
        customer_name: "Solicitacao-Estoque",
        order_number: orderNumber,
        logistics_status: "SEM ETIQUETA",
        archived: false,
        certificatePrinted: false,
        isPE: true,
        isStockRequest: true,
        specs: {
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "bruta",
          size: size || null,
          gaveta: gaveta || "",
          exception: ""
        },
        full_data: {
          order: { number: orderNumber, customer: { name: "Solicitacao-Estoque" } },
          sku: clean,
          status: "INJETADO",
          production_step: "INJETADO",
          isPE: true,
          isStockRequest: true,
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "bruta",
          size: size || null,
          productName: productName || "",
          gaveta: gaveta || "",
          createdAt: new Date().toISOString()
        }
      };
      const rec = await pb.collection("production_orders").create(payload);
      createdOrders.push(rec);
    }

    for (let i = 0; i < numFundirPrecravada; i++) {
      const payload = {
        status: "INJETADO",
        production_step: "INJETADO",
        sku: clean,
        customer_name: "Solicitacao-Estoque",
        order_number: orderNumber,
        logistics_status: "SEM ETIQUETA",
        archived: false,
        certificatePrinted: false,
        isPE: true,
        isStockRequest: true,
        specs: {
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "precravada",
          size: size || null,
          gaveta: gaveta || "",
          exception: ""
        },
        full_data: {
          order: { number: orderNumber, customer: { name: "Solicitacao-Estoque" } },
          sku: clean,
          status: "INJETADO",
          production_step: "INJETADO",
          isPE: true,
          isStockRequest: true,
          processo_fabricacao: "borracha",
          origem: "subestoque_cera",
          tipo_cera: "precravada",
          size: size || null,
          productName: productName || "",
          gaveta: gaveta || "",
          createdAt: new Date().toISOString()
        }
      };
      const rec = await pb.collection("production_orders").create(payload);
      createdOrders.push(rec);
    }

    // 2. Cria itens para Injetar e Produzir (status: IMPRIMIR)
    for (let i = 0; i < numInjetar; i++) {
      const payload = {
        status: "IMPRIMIR",
        production_step: "IMPRIMIR",
        sku: clean,
        customer_name: "Solicitacao-Estoque",
        order_number: orderNumber,
        logistics_status: "SEM ETIQUETA",
        archived: false,
        certificatePrinted: false,
        isPE: true,
        isStockRequest: true,
        specs: {
          processo_fabricacao: "borracha",
          origem: "solicitacao_estoque",
          tipo_cera: injetarTipoCera,
          size: size || null,
          gaveta: gaveta || "",
          exception: ""
        },
        full_data: {
          order: { number: orderNumber, customer: { name: "Solicitacao-Estoque" } },
          sku: clean,
          status: "IMPRIMIR",
          production_step: "IMPRIMIR",
          isPE: true,
          isStockRequest: true,
          processo_fabricacao: "borracha",
          origem: "solicitacao_estoque",
          tipo_cera: injetarTipoCera,
          size: size || null,
          productName: productName || "",
          gaveta: gaveta || "",
          createdAt: new Date().toISOString()
        }
      };
      const rec = await pb.collection("production_orders").create(payload);
      createdOrders.push(rec);
    }

    // 3. Atualiza subestoque (debita apenas o que foi fundido) e registra histórico
    const cleanSize = size ? String(size).trim() : null;
    let motivo = `Ordem #${orderNumber} (${totalGeral} un total: `;
    const parts = [];
    if (numFundirTotal > 0) {
      parts.push(`${numFundirTotal} un direto p/ Fundição [${numFundirBruta}b/${numFundirPrecravada}c]`);
    }
    if (numInjetar > 0) {
      parts.push(`${numInjetar} un p/ Injetar [${injetarTipoCera}]`);
    }
    motivo += parts.join(" + ");
    if (cleanSize) {
      motivo += ` no Aro ${cleanSize}`;
    }
    motivo += ")";

    const debitParams = numFundirTotal > 0 ? (
      cleanSize ? {
        deltaAro: {
          aro: cleanSize,
          deltaBruta: -numFundirBruta || 0,
          deltaPrecravada: -numFundirPrecravada || 0
        }
      } : {
        deltaBruta: -numFundirBruta || 0,
        deltaPrecravada: -numFundirPrecravada || 0
      }
    ) : {};

    const debitRes = await produtoDadosService.updateSubestoqueCera(skuPai, {
      ...debitParams,
      tipoOperacao: numFundirTotal > 0 ? "ENVIO_FUNDICAO_ESTOQUE" : "SOLICITACAO_INJECAO_ESTOQUE",
      motivo,
      usuario: userName
    });

    return {
      success: true,
      orderNumber,
      fundirCount: numFundirTotal,
      injetarCount: numInjetar,
      totalQtd: totalGeral,
      createdOrders,
      updatedSubestoque: debitRes?.subestoque || null
    };
  },

  /**
   * Solicitação direta e unificada de produção de estoque via molde:
   * Apenas informa o item, quantidade e medida (se anel).
   * Quem decide na fábrica se aproveita cera da gaveta ou injeta nova
   * é a equipe de produção diretamente na fila de bancada.
   */
  async solicitarProducaoEstoque({
    sku,
    productName = "",
    qtd = 1,
    size = null,
    gaveta = "",
    observacao = "",
    userName = "Operador"
  }) {
    const numQtd = Math.max(1, parseInt(qtd, 10) || 1);
    if (!sku) {
      throw new Error("SKU inválido para solicitação de produção.");
    }

    const clean = cleanSku(sku);
    const skuPai = getBaseSku(clean) || clean;
    const orderNumber = generateEstoqueOrderNumber();
    const createdOrders = [];

    for (let i = 0; i < numQtd; i++) {
      const payload = {
        status: "IMPRIMIR",
        production_step: "IMPRIMIR",
        sku: clean,
        customer_name: "Solicitacao-Estoque",
        order_number: orderNumber,
        logistics_status: "SEM ETIQUETA",
        archived: false,
        certificatePrinted: false,
        isPE: true,
        isStockRequest: true,
        specs: {
          processo_fabricacao: "borracha",
          origem: "solicitacao_estoque",
          size: size || null,
          gaveta: gaveta || "",
          observacao: observacao || "",
          exception: ""
        },
        full_data: {
          order: {
            number: orderNumber,
            customer: {
              name: "Solicitacao-Estoque"
            }
          },
          sku: clean,
          status: "IMPRIMIR",
          production_step: "IMPRIMIR",
          isPE: true,
          isStockRequest: true,
          processo_fabricacao: "borracha",
          origem: "solicitacao_estoque",
          size: size || null,
          productName: productName || "",
          gaveta: gaveta || "",
          observacao: observacao || "",
          createdAt: new Date().toISOString()
        }
      };

      const record = await pb.collection("production_orders").create(payload);
      createdOrders.push(record);
    }

    // Registra no histórico do molde
    const cleanSize = size ? String(size).trim() : null;
    const obsTxt = observacao ? ` - Obs: ${observacao}` : "";
    await produtoDadosService.updateSubestoqueCera(skuPai, {
      tipoOperacao: "SOLICITACAO_ESTOQUE",
      motivo: `Solicitação de Estoque #${orderNumber} (${numQtd} un${cleanSize ? ` no Aro ${cleanSize}` : ""}${obsTxt})`,
      usuario: userName
    });

    return {
      success: true,
      orderNumber,
      totalQtd: numQtd,
      createdOrders
    };
  }
};
